/**
 * Plain-English decoder for VSX / GCVS variability-type codes.
 *
 * VSX returns terse codes (UG, EA, M, CBSS/V+E) that only mean something if you
 * already know the GCVS scheme. This maps the common ones to a short human label so
 * the UI can show "Dwarf nova" instead of "UG", while keeping the raw code available
 * (e.g. in a tooltip) for people who do read the codes. Compound types (joined by
 * "/" or "+") are decoded component-by-component; unknown codes fall back to the raw
 * text so nothing is ever hidden.
 */

// Concise labels for the primary/common types. Not exhaustive — unknowns pass through.
const TYPE_MAP = {
  // Cataclysmic / novae / symbiotic
  UG: 'Dwarf nova', UGSS: 'Dwarf nova (SS Cyg)', UGSU: 'Dwarf nova (SU UMa)',
  UGZ: 'Dwarf nova (Z Cam)', UGWZ: 'Dwarf nova (WZ Sge)',
  N: 'Nova', NA: 'Fast nova', NB: 'Slow nova', NC: 'Very slow nova',
  NL: 'Nova-like variable', NR: 'Recurrent nova',
  ZAND: 'Symbiotic variable', AM: 'Polar (AM Her)', DQ: 'Intermediate polar (DQ Her)',
  CBSS: 'Close-binary supersoft source', CV: 'Cataclysmic variable',
  SN: 'Supernova', SNI: 'Type I supernova', SNII: 'Type II supernova',
  // Eclipsing binaries
  E: 'Eclipsing binary', EA: 'Eclipsing binary (Algol)', EB: 'Eclipsing binary (β Lyr)',
  EW: 'Eclipsing binary (W UMa)', EP: 'Eclipsing by a planet', ELL: 'Ellipsoidal binary',
  // Pulsating
  M: 'Mira (long-period)', SR: 'Semiregular', SRA: 'Semiregular (SRa)', SRB: 'Semiregular (SRb)',
  SRC: 'Semiregular supergiant', SRD: 'Semiregular (SRd)',
  L: 'Slow irregular', LB: 'Slow irregular (giant)', LC: 'Slow irregular (supergiant)',
  RR: 'RR Lyrae', RRAB: 'RR Lyrae (RRab)', RRC: 'RR Lyrae (RRc)',
  CEP: 'Cepheid', DCEP: 'Classical Cepheid', DCEPS: 'Cepheid (low-amplitude)', CW: 'Type II Cepheid',
  DSCT: 'Delta Scuti', GDOR: 'Gamma Doradus', RV: 'RV Tauri', BCEP: 'Beta Cephei',
  ZZ: 'Pulsating white dwarf', ZZA: 'Pulsating WD (ZZ Cet)', ACYG: 'Alpha Cygni',
  // Rotating
  ACV: 'Alpha² CVn (rotating)', BY: 'BY Dra (spotted)', RS: 'RS CVn (active binary)',
  ROT: 'Rotating variable', ELV: 'Ellipsoidal',
  // Eruptive
  UV: 'Flare star (UV Cet)', UVN: 'Flare star (Orion)', GCAS: 'Gamma Cas (Be)', BE: 'Be star',
  WR: 'Wolf-Rayet', RCB: 'R CrB', INT: 'T Tauri (Orion)', IN: 'Orion variable', FU: 'FU Ori',
  // X-ray
  X: 'X-ray binary', XB: 'X-ray burster', XP: 'X-ray pulsar', XRB: 'X-ray binary',
  // Generic
  VAR: 'Variable star',
};

function decodeToken(tok) {
  // Strip qualifiers: trailing ':' (uncertain), '?' , and any parenthetical.
  const key = tok.replace(/[:?]/g, '').replace(/\(.*?\)/g, '').trim().toUpperCase();
  return { key, label: TYPE_MAP[key] || null };
}

/**
 * @param {string} code raw VSX VariabilityType, e.g. "UG", "CBSS/V+E"
 * @returns {{short: string, full: string, uncertain: boolean}|null}
 *   short: concise label for the primary component (chip text)
 *   full:  "<raw code> — <decoded components>" (tooltip)
 */
export function describeVariabilityType(code) {
  if (!code || typeof code !== 'string') return null;
  const raw = code.trim();
  if (!raw) return null;
  const tokens = raw.split(/[/+]/).map(t => t.trim()).filter(Boolean);
  const decoded = tokens.map(decodeToken);
  const primary = decoded.find(d => d.label) || decoded[0];
  const short = (primary && primary.label) || raw;
  const parts = decoded.map(d => d.label || d.key);
  const full = parts.every((p, i) => p === decoded[i].key)
    ? raw // nothing decoded — don't echo a redundant "X — X"
    : `${raw} — ${parts.join(' + ')}`;
  return { short, full, uncertain: /:/.test(raw) };
}
